import React, { useState, useEffect } from "react";
import hosys from "@/api/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";

const TYPE_LABELS = {
  temperature_check: "Revisió de temperatures",
  cleaning_check: "Revisió de neteja",
  document_check: "Comprovació documental",
  equipment_check: "Verificació d'equips",
  goods_reception: "Control de recepció de mercaderies",
  oil_control: "Control d'olis",
  pest_control: "Control de plagues",
  preventive_maintenance: "Manteniment preventiu",
};

const FREQ_LABELS = {
  daily: "Diària",
  weekly: "Setmanal",
  monthly: "Mensual",
  quarterly: "Trimestral",
  annual: "Anual",
  on_demand: "A demanda",
};

const RESULT_LABELS = {
  pending: "Pendent",
  correct: "Correcta",
  incorrect: "Incorrecta",
  not_applicable: "No aplica",
};

export default function VerificationForm({ open, onClose, onSave, venueId, user, editData }) {
  const today = new Date().toISOString().split("T")[0];
  const empty = {
    venue_id: venueId || "",
    name: "",
    type: "",
    description: "",
    frequency: "monthly",
    responsible_name: user?.full_name || "",
    responsible_email: user?.email || "",
    planned_date: today,
    completed_date: "",
    status: "pending",
    observations: "",
    corrective_action: "",
    is_template: false,
  };

  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [createIncident, setCreateIncident] = useState(false);

  useEffect(() => {
    if (open) {
      setForm(editData ? { ...empty, ...editData } : { ...empty, venue_id: venueId || "", responsible_name: user?.full_name || "", responsible_email: user?.email || "" });
      setCreateIncident(false);
    }
  }, [open, editData]);

  const set = (key, val) => setForm(f => ({ ...f, [key]: val }));

  const handleSave = async () => {
    setSaving(true);
    await onSave(form, createIncident && form.status === "incorrect");
    setSaving(false);
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{editData ? "Editar verificació" : "Nova verificació"}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <Label>Nom *</Label>
              <Input value={form.name} onChange={e => set("name", e.target.value)} placeholder="Ex: Control nevera principal" />
            </div>
            <div>
              <Label>Tipus *</Label>
              <Select value={form.type} onValueChange={v => set("type", v)}>
                <SelectTrigger><SelectValue placeholder="Selecciona tipus" /></SelectTrigger>
                <SelectContent>
                  {Object.entries(TYPE_LABELS).map(([k, v]) => (
                    <SelectItem key={k} value={k}>{v}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Periodicitat *</Label>
              <Select value={form.frequency} onValueChange={v => set("frequency", v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(FREQ_LABELS).map(([k, v]) => (
                    <SelectItem key={k} value={k}>{v}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Data prevista *</Label>
              <Input type="date" value={form.planned_date} onChange={e => set("planned_date", e.target.value)} />
            </div>
            <div>
              <Label>Responsable</Label>
              <Input value={form.responsible_name} onChange={e => set("responsible_name", e.target.value)} placeholder="Nom del responsable" />
            </div>
          </div>

          <div>
            <Label>Descripció</Label>
            <Textarea value={form.description} onChange={e => set("description", e.target.value)} placeholder="Descripció del control..." rows={2} />
          </div>

          <div className="border-t pt-4">
            <Label className="text-sm font-semibold text-gray-700 mb-2 block">Resultat de la verificació</Label>
            <div className="grid grid-cols-2 gap-2">
              {Object.entries(RESULT_LABELS).map(([k, v]) => {
                const colors = {
                  pending: "border-gray-300 text-gray-600",
                  correct: "border-emerald-400 text-emerald-700 bg-emerald-50",
                  incorrect: "border-red-400 text-red-700 bg-red-50",
                  not_applicable: "border-gray-300 text-gray-500",
                };
                const active = form.status === k;
                return (
                  <button
                    key={k}
                    type="button"
                    onClick={() => set("status", k)}
                    className={`px-3 py-2 rounded-xl border-2 text-sm font-medium transition-all ${active ? colors[k] + " ring-2 ring-offset-1 ring-current" : "border-gray-200 text-gray-500 hover:border-gray-300"}`}
                  >
                    {v}
                  </button>
                );
              })}
            </div>
          </div>

          {form.status !== "pending" && (
            <div>
              <Label>Data de realització</Label>
              <Input type="date" value={form.completed_date || new Date().toISOString().split("T")[0]} onChange={e => set("completed_date", e.target.value)} />
            </div>
          )}

          <div>
            <Label>Observacions</Label>
            <Textarea value={form.observations} onChange={e => set("observations", e.target.value)} placeholder="Notes o observacions..." rows={2} />
          </div>

          {form.status === "incorrect" && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-3 space-y-3">
              <p className="text-sm font-semibold text-red-700">⚠ Verificació incorrecta</p>
              <div>
                <Label>Acció correctiva</Label>
                <Textarea
                  value={form.corrective_action}
                  onChange={e => set("corrective_action", e.target.value)}
                  placeholder="Descriu l'acció correctiva a aplicar..."
                  rows={2}
                />
              </div>
              <label className="flex items-center gap-2 text-sm text-red-700 cursor-pointer">
                <input
                  type="checkbox"
                  checked={createIncident}
                  onChange={e => setCreateIncident(e.target.checked)}
                  className="rounded"
                />
                Crear incidència vinculada automàticament
              </label>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel·lar</Button>
          <Button onClick={handleSave} disabled={saving || !form.name || !form.type || !form.planned_date} className="bg-[#7B2D3F] hover:bg-[#5B1D2F]">
            {saving ? "Desant..." : "Desar"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}